import client from "../../configs/graphql.config";
import Loading from "../../components/Loading";
import { START_SIGNATURE_PROCESS, RESET_SIGNATURE_PROCESS } from "../../graphql/skribble";
import Avatar2Svg from "../../assets/images/avatar_2.svg";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { CalloutMode } from "../../ui_atoms/Callout";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { ContractSignatureStatus, SkribbleSigningState } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import MarkdownView from "../../components/MarkdownView";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import React, { use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Image, Linking, TextStyle, Platform, StyleSheet } from "react-native";
import { useIntercom } from "../../hooks/intercom";
import Button, { ButtonMode } from "../../components/Button";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import { GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../../graphql/activity";
import WebViewWrapper from "../../components/WebViewWrapper";
import { useTheme } from "../../hooks/use-theme";
import { LiftTheme } from "../../theme";
import Text from "../../components/Text";
import { useTracking } from "../../hooks/tracking";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { useContract } from "../../hooks/use-contract";
import {
    isCancelEndUrls,
    isDeclineEndUrls,
    isFailEndUrls,
    isLegacySuccessEndUrl,
    isSuccessEndUrls,
} from "../../skribble/helpers";
import { useSnackbar } from "../../hooks/snackbar";
import { useAssetWaiting } from "../../hooks/assets";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import * as Sentry from "@sentry/react-native";
import { kycGradient } from "../../configs/linear-gradients";
import { BubbleTalk } from "../../components/BubbleTalk";
import { TitleMediumText } from "../../components/FormattedText";
import { GET_USER } from "../../graphql/identity";
import { GET_CONTRACT } from "../../graphql/core";
import { getContractLabelKind, sleep } from "../../utils";

const IS_REDIRECT_MODE = Platform.OS === "web";

const FINALIZATION_POLLING_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

export type ContractSigningCallbackStatus = "success" | "decline" | "unknown";

type ContractSigningScreenProps = StackScreenProps<RootStackParamList, "ContractSigningScreen">;

type MessageInfo = {
    text: string;
    mode: CalloutMode;
};

export type ActionType = "start" | "goHome" | "goMission" | "retry" | "canceled";

const MESSAGE_INFO_STYLE: Record<CalloutMode, (theme: LiftTheme) => TextStyle> = {
    info: (theme) => ({ color: theme.colors.primary }),
    success: (theme) => ({ color: theme.colors.onSuccessBackground }),
    error: (theme) => ({ color: theme.colors.onErrorBackground }),
    warning: (theme) => ({ color: theme.colors.onWarningBackground }),
    secondary: (theme) => ({ color: theme.colors.secondary }),
};

const MessageInfoDisplay: React.FC<{ messageInfo: MessageInfo }> = ({ messageInfo }) => {
    const theme = useTheme();
    return (
        <Text
            style={[
                { ...theme.fonts.bodyMedium, marginVertical: theme.gridSize, textAlign: "center" },
                MESSAGE_INFO_STYLE[messageInfo.mode](theme),
            ]}
        >
            {messageInfo.text}
        </Text>
    );
};

const ContractSigningScreen: React.FC<ContractSigningScreenProps> = ({ route, navigation }) => {
    const i18n = useI18n();
    const currentContractId = useRef<string>(undefined);
    const { colors } = useTheme();
    const styles = useStyles();
    const { user } = use(AuthManagerContext);
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { contractId, initialState = null, contractType = "contract", status } = route.params;

    // when signed, hold the asset Id to check
    const [waitingAssetId, setWaitingAssetId] = useState<string>();

    // information display to the user
    const [messageInfo, setMessageInfo] = useState<MessageInfo | null>(null); // [message, mode]

    // action available to the user on the bottom button
    const [actionType, setActionType] = useState<ActionType | null>(initialState);

    // current displayed webView
    const [webViewUrl, setWebviewUrl] = useState<string | null>(null);

    const { showSnackbar } = useSnackbar();
    // true while initializeSigning is getting information from the API
    const [initializing, setInitializing] = useState<boolean>(false);

    // Set to true when startSignatureProcess errors or returns no usable
    // payload — drives the dedicated retry screen below (BAS-2672). Tracked
    // as local state rather than relying on the mutation hook's `error` so we
    // can clear it precisely at the start of every retry attempt, regardless
    // of how Apollo populates / clears its own error state.
    const [sessionCreationFailed, setSessionCreationFailed] = useState(false);

    // true while polling the contract's signatureStatus from the backend after signing is completed
    const [finalizing, setFinalizing] = useState(false);

    const { trackEvent } = useTracking();
    const { presentIntercom } = useIntercom();

    // hold the asset when signing is completed
    // DO not pull asset when the webview is active.
    const { waiting, asset } = useAssetWaiting(webViewUrl ? undefined : waitingAssetId);

    const { contract, loading: getContractLoading } = useContract(
        contractId,
        contractType,
        messageInfo?.mode,
    );

    // Track the contract's effective asset id whenever it changes upstream.
    // Kept as an effect rather than an "adjust state on prop change" render-
    // phase block because the guard there is false on the first render (prev
    // seeded equal to current), which would skip the initial set on Apollo
    // cache-and-network hits — and the contract is essentially always cached
    // when arriving here from ContractPreviewScreen.signNow.
    useEffect(() => {
        if (contract.clientEffectiveAsset?.id) {
            setWaitingAssetId(contract.clientEffectiveAsset.id);
        }
    }, [contract.clientEffectiveAsset?.id]);

    const [startSignatureProcess, { error, loading, reset: resetStartSignatureProcess }] =
        useMutation(START_SIGNATURE_PROCESS, { errorPolicy: "all" });
    const [resetSignatureProcess, { error: resetError, loading: reseting }] =
        useMutation(RESET_SIGNATURE_PROCESS);
    const goToMission = useCallback(() => {
        if (!contractId || navigation.getState().routes.length === 0) {
            navigation.popToTop();
        } else {
            if (navigation.getState().routes[0].name === "Home") {
                let screenName = "MissionsScreen" as const;
                if (
                    navigation.getState().routes[0].state?.routeNames &&
                    navigation.getState().routes[0].state?.index !== undefined
                ) {
                    screenName =
                        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        navigation.getState().routes[0].state.routeNames[
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            navigation.getState().routes[0].state.index
                        ];
                }
                navigation.popTo("Home", {
                    screen: screenName,
                    params: {
                        screen: "MissionDetails",
                        params: {
                            missionId: contract?.missionInfo?.id || "",
                        },
                    },
                });
            } else {
                navigation.popToTop();
            }
        }
    }, [contract?.missionInfo?.id, navigation, contractId]);

    const refreshQueries = useCallback(async () => {
        await client.refetchQueries({
            include: [
                FIND_MY_MISSIONS,
                GET_ACTIVITY_TODO_FEED,
                GET_ALL_PROFILES_ACTIVITIES_INFO,
                GET_USER,
            ],
        });
    }, []);

    const [fetchContract] = useLazyQuery(GET_CONTRACT, { fetchPolicy: "no-cache" });

    const onStatusUpdate = useCallback(
        async (newStatus: ContractSigningCallbackStatus) => {
            setWebviewUrl(null);

            let finalizationTimedOut = false;
            if (newStatus === "success") {
                // Polling the contract every 1 second to wait until the new signatureStatus is applied on the back-end
                setFinalizing(true);
                finalizationTimedOut = true;
                const startTime = Date.now();
                while (Date.now() - startTime < FINALIZATION_POLLING_TIMEOUT_MS) {
                    try {
                        const result = await fetchContract({
                            variables: { id: contractId },
                        });
                        if (
                            result.data?.getContract?.signatureStatus !==
                            ContractSignatureStatus.WaitingSelfSign
                        ) {
                            finalizationTimedOut = false;
                            break;
                        }
                    } catch {
                        // Ignore transient errors and keep polling until the timeout fires
                    }
                    await sleep(1000);
                }
                const signedMissionId = contract?.missionInfo?.id;
                if (signedMissionId) {
                    client.cache.evict({
                        fieldName: "getMission",
                        args: { id: signedMissionId },
                    });
                    client.cache.gc();
                }
                setFinalizing(false);
            }

            if (!finalizationTimedOut) {
                showSnackbar({
                    text: i18n.t(
                        `screens.contract_signing.signature_status_update.${contractType}.${profileType}.${newStatus}`,
                    ),
                    mode: newStatus === "success" ? "success" : "info",
                    timeout: 7000,
                });
            } else {
                showSnackbar({
                    text: i18n.t("screens.contract_signing.finalization_pending"),
                    mode: "info",
                    timeout: 7000,
                });
            }

            void refreshQueries();
            if (contractType === "client_framework") {
                const clientFrameworkParams = {
                    screen: "ProfileStack",
                    params: {
                        screen: "Profile",
                    },
                } as const;
                if (IS_REDIRECT_MODE) {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "Home",
                                params: clientFrameworkParams,
                            },
                        ],
                    });
                } else {
                    navigation.popTo("Home", clientFrameworkParams);
                }
            } else {
                goToMission();
            }
        },
        [
            showSnackbar,
            i18n,
            contractType,
            profileType,
            refreshQueries,
            fetchContract,
            contractId,
            navigation,
            goToMission,
            contract?.missionInfo?.id,
        ],
    );

    /**
     * Initialize the signing process, call the API to get or create the signing basket
     * and update state accordingly
     */
    const initializeSigning = useCallback(
        async (contractId: string) => {
            setMessageInfo(null);
            setWebviewUrl(null);
            setActionType("goHome");
            setSessionCreationFailed(false);

            let gqlResult;
            try {
                setInitializing(true);
                gqlResult = await startSignatureProcess({
                    variables: { contractId },
                    fetchPolicy: "network-only",
                });
            } catch (err) {
                // Apollo v4 with `errorPolicy: "all"` resolves on GraphQL/server
                // errors but still throws on low-level transport errors; surface
                // both paths to the retry screen (BAS-2672).
                setSessionCreationFailed(true);
                Sentry.captureException(err);
                setInitializing(false);
                return;
            }
            try {
                if (gqlResult.data) {
                    const procObj = gqlResult.data.startSignatureProcess;
                    switch (procObj.signState) {
                        case SkribbleSigningState.Declined:
                            setMessageInfo({
                                text: i18n.t(
                                    "screens.contract_signing.state_message.sign_declined",
                                ),
                                mode: "error",
                            });
                            setActionType("goMission");
                            void trackEvent("contract_signing_declined");
                            break;
                        case SkribbleSigningState.Withdrawn:
                            setMessageInfo({
                                text: i18n.t(
                                    "screens.contract_signing.state_message.sign_withdrawn",
                                ),
                                mode: "error",
                            });
                            setActionType("goMission");
                            void trackEvent("contract_signing_withdraw");
                            break;
                        case SkribbleSigningState.Open:
                            if (procObj.signUrl) {
                                setWebviewUrl(procObj.signUrl);
                                setWaitingAssetId(procObj.signedAssetId || undefined);
                            } else if (procObj.emailSent) {
                                setMessageInfo({
                                    text: i18n.t(
                                        "screens.contract_signing.state_message.email_sent",
                                        { email: user?.email },
                                    ),
                                    mode: "info",
                                });
                            } else {
                                // no signUrl and no email sent, this is an error
                                setMessageInfo({
                                    text: i18n.t(
                                        "screens.contract_signing.state_message.sign_error",
                                    ),
                                    mode: "error",
                                });
                                void trackEvent("contract_signing_error");
                                Sentry.captureException(
                                    new Error(
                                        "Contract signing error. No signUrl and no email sent",
                                    ),
                                );
                                setActionType("goMission");
                            }
                            break;
                        case SkribbleSigningState.Signed:
                            setWaitingAssetId(procObj.signedAssetId || undefined);
                            void trackEvent("contract_signing_success");
                            // onSuccess(); will be called when asset is ready
                            break;
                        default:
                            throw new Error("Unknown signing state");
                    }
                } else if (gqlResult.error) {
                    // Server returned a GraphQL error (or Apollo resolved with
                    // `data: undefined, error` under errorPolicy: "all"): drive
                    // the dedicated retry screen below (BAS-2672).
                    setSessionCreationFailed(true);
                    Sentry.captureException(gqlResult.error);
                } else {
                    // No data and no errors — should never happen, but treat
                    // it as a session-creation failure so the user can retry.
                    setSessionCreationFailed(true);
                    Sentry.captureException(
                        new Error("startSignatureProcess: no data and no errors in payload"),
                    );
                }
            } finally {
                setInitializing(false);
            }
        },
        [i18n, startSignatureProcess, trackEvent, user?.email],
    );

    const retryInitializeSigning = useCallback(async () => {
        resetStartSignatureProcess();
        await initializeSigning(contractId);
    }, [contractId, initializeSigning, resetStartSignatureProcess]);

    useEffect(() => {
        const listener = Linking.addEventListener("url", (e) => {
            // eslint-disable-next-line no-console
            console.log("ContractSigningScreen link url received --> ", e);
            // we receive a link notification, we should remove the webview
            setWebviewUrl(null);
            // we restart the initialization process to get back the state
            // of the signing process and act accordingly
            void initializeSigning(contractId);
        });
        return () => {
            listener.remove();
        };
    }, [initializeSigning, contractId]);

    const resetSigning = useCallback(async () => {
        await resetSignatureProcess({ variables: { contractId: contractId } });
        await initializeSigning(contractId);
    }, [resetSignatureProcess, initializeSigning, contractId]);

    /**
     * Entry point of the screen, if the parameter change, restart the initialization process
     */
    useEffect(() => {
        if (
            contractId !== currentContractId.current &&
            !initialState &&
            !status &&
            contract.signatureStatus !== ContractSignatureStatus.Success
        ) {
            currentContractId.current = contractId;
            void initializeSigning(contractId);
        }
    }, [initialState, initializeSigning, contractId, contract.signatureStatus, status]);

    useEffect(() => {
        if (
            [
                ContractSignatureStatus.CanceledBySelf,
                ContractSignatureStatus.CanceledByLse,
            ].includes(
                (contract?.signatureStatus as ContractSignatureStatus) ||
                    ContractSignatureStatus.WaitingContract,
            )
        ) {
            goToMission();
        }
    }, [contract?.signatureStatus, goToMission]);

    const onBottomAction = useCallback(async () => {
        switch (actionType) {
            case "goHome":
                void refreshQueries();
                navigation.reset({
                    index: 0,
                    routes: [{ name: "Home" }],
                });
                break;
            case "goMission":
                void refreshQueries();
                goToMission();
                break;
            case "canceled":
                void refreshQueries();
                if (contractType === "client_framework") {
                    navigation.popTo("Home", {
                        screen: "ProfileStack",
                        params: {
                            screen: "Profile",
                        },
                    });
                } else {
                    navigation.popTo("Home", {
                        screen: "MissionsScreen",
                        params: {
                            screen: "MissionDetails",
                            params: {
                                missionId: contract?.missionInfo?.id || "",
                            },
                        },
                    });
                }
                break;
            case "retry":
                await resetSigning();
                break;
        }
    }, [
        actionType,
        contract?.missionInfo?.id,
        contractType,
        goToMission,
        navigation,
        refreshQueries,
        resetSigning,
    ]);

    const onBottomActionTwo = useCallback(() => {
        switch (actionType) {
            case "canceled":
                void refreshQueries();
                void initializeSigning(contractId);
                break;
        }
    }, [actionType, initializeSigning, refreshQueries, contractId]);

    const [buttonMode, buttonTitle]: [ButtonMode?, string?] = useMemo(() => {
        switch (actionType) {
            case "start":
                return ["primary", i18n.t("screens.contract_signing.start")];
            case "goHome":
                return ["outlined", i18n.t("screens.contract_signing.go_home")];
            case "goMission":
                return ["outlined", i18n.t("screens.contract_signing.back")];
            case "retry":
                return ["primary", i18n.t("screens.contract_signing.reset_signing")];
            case "canceled":
                return ["outlined", i18n.t("screens.contract_signing.postpone_signing")];
            default:
                return ["primary", undefined];
        }
    }, [actionType, i18n]);

    const [buttonModeTwo, buttonTitleTwo]: [ButtonMode?, string?] = useMemo(() => {
        switch (actionType) {
            case "canceled":
                return ["primary", i18n.t("screens.contract_signing.retry_signing")];
            default:
                return [undefined, undefined];
        }
    }, [actionType, i18n]);

    const messageComponent = useMemo(() => {
        switch (actionType) {
            case "canceled":
                return (
                    <BubbleTalk
                        personImageComponent={<Avatar2Svg width="100%" height="100%" />}
                        position="top-center"
                        bubbleBackgroundColor={colors.background}
                    >
                        <TitleMediumText>
                            {i18n.t("screens.contract_signing.state_message.sign_canceled")}
                        </TitleMediumText>
                    </BubbleTalk>
                );
            default:
                return null;
        }
    }, [actionType, colors.background, i18n]);

    useEffect(() => {
        if (IS_REDIRECT_MODE && webViewUrl) {
            navigation.replace("ContractSigningScreen", {
                ...route.params,
                initialState: "canceled",
            });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.location.href = webViewUrl;
        }
    }, [asset, navigation, route.params, webViewUrl]);

    useEffect(() => {
        if (actionType === "canceled") {
            navigation.setOptions({ headerShown: false });
        }
    }, [actionType, navigation]);

    useEffect(() => {
        if (IS_REDIRECT_MODE) {
            // status param come from the back-end (after signature/reject)
            // setTimeout is necessary as it seems that the navigation state is not fully ready yet on first render.
            if (status && !getContractLoading) {
                setTimeout(() => {
                    void onStatusUpdate(status);
                }, 0);
            }
        }
    }, [onStatusUpdate, status, getContractLoading]);

    // when the signature is canceled, the waiting for asset is still running
    // but we obviously never receive it.
    // we don't want to display this "waiting" message when user canceled the signing process
    const displayWaiting = waiting && actionType !== "canceled";

    // Dedicated retry screen when the signature session creation failed
    // (network error / 5xx). See BAS-2672.
    // Placed BEFORE the `IS_REDIRECT_MODE` early-return so the retry UI is
    // reachable on web too — otherwise web users get stuck on the Loading
    // spinner that's normally used while the browser is being redirected.
    if (sessionCreationFailed && !initializing) {
        return (
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("screens.contract_signing.retry"),
                        mode: "primary",
                        onPress: retryInitializeSigning,
                        validating: loading,
                    },
                ]}
            >
                <VStack h={1} gap={1} alignment="stretch" style={{ justifyContent: "center" }}>
                    <Text style={styles.errorText}>
                        {i18n.t("screens.contract_signing.session_creation_error")}
                    </Text>
                </VStack>
            </ScreenCard>
        );
    }
    if (IS_REDIRECT_MODE) {
        return (
            <Loading style={{ height: "100%", alignSelf: "center", justifyContent: "center" }} />
        );
    }
    if (actionType == "retry") {
        return (
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("screens.contract_signing.reset_signing"),
                        mode: "primary",
                        onPress: resetSigning,
                        validating: loading || reseting,
                    },
                ]}
                headband={
                    resetError
                        ? {
                              text: i18n.t("screens.contract_signing.reset_error"),
                              mode: "error",
                          }
                        : undefined
                }
            >
                <VStack gap={1} h="2/3">
                    <Image source={require("../../assets/images/404-image.png")} />
                    <VStack gap={1}>
                        <Button
                            buttonMode="outlined"
                            label={i18n.t("screens.contract_signing.contact_support")}
                            processing={reseting}
                            onPress={() => {
                                void presentIntercom();
                            }}
                        />
                    </VStack>
                </VStack>
            </ScreenCard>
        );
    }
    if (webViewUrl) {
        return (
            <WebViewWrapper
                url={webViewUrl}
                id="signing-frame"
                enableBack
                getDownloadFileName={() => {
                    const contractLabelKind = getContractLabelKind(
                        profileType,
                        contract?.missionInfo?.clientInfo?.useLse,
                    );
                    return i18n.t(
                        `mission.contract_names.${contractLabelKind}-${
                            currentContractId.current || "0"
                        }.pdf`,
                    );
                }}
                onUrlChanged={(url) => {
                    // we log this to be able to display the url previously called
                    // as sentry does keep this information when generating a report
                    // eslint-disable-next-line no-console
                    console.log("Signing url changed --> ", `>${route.path}<`, url);
                    if (isSuccessEndUrls(url)) {
                        void onStatusUpdate("success");
                    } else if (isDeclineEndUrls(url)) {
                        void onStatusUpdate("decline");
                    } else if (isLegacySuccessEndUrl(url)) {
                        void onStatusUpdate("success");
                    } else if (isCancelEndUrls(url)) {
                        setWebviewUrl(null);
                        setActionType("canceled");
                    } else if (isFailEndUrls(url)) {
                        // problem in skribble signing process
                        setWebviewUrl(null);
                        setActionType("goMission");
                        setMessageInfo({
                            text: i18n.t("screens.contract_signing.state_message.sign_failed"),
                            mode: "error",
                        });
                    } else if (url.indexOf("/storybook/close-web-view") !== -1) {
                        // case only for storybook
                        setWebviewUrl(null);
                        setMessageInfo({
                            text: "WebView closed in storybook",
                            mode: "info",
                        });
                    }
                }}
            />
        );
    }

    return (
        <ScreenCard
            isContentContainerFlex
            buttons={
                !!buttonTitle &&
                !initializing &&
                !displayWaiting &&
                !finalizing && [
                    ...(buttonTitleTwo
                        ? [
                              {
                                  title: buttonTitleTwo,
                                  mode: buttonModeTwo,
                                  validating: loading,
                                  onPress: onBottomActionTwo,
                              },
                          ]
                        : []),
                    {
                        title: buttonTitle,
                        mode: buttonMode,
                        validating: loading,
                        onPress: onBottomAction,
                    },
                ]
            }
            headband={
                error
                    ? {
                          text: i18n.t("screens.contract_signing.signing_error"),
                          mode: "error",
                      }
                    : undefined
            }
            backgroundGradient={actionType === "canceled" ? kycGradient : undefined}
        >
            {displayWaiting || loading || initializing || finalizing ? (
                <VStack gap={1.5} style={{ alignItems: "center" }}>
                    <Loading />
                    <Text style={styles.loadingText}>
                        {waiting
                            ? i18n.t("contract.signing_status.waiting_contract_asset")
                            : finalizing
                              ? i18n.t("screens.contract_signing.finalizing")
                              : i18n.t("screens.contract_signing.loading")}
                    </Text>
                </VStack>
            ) : (
                <VStack
                    h={1}
                    gap={1}
                    alignment="stretch"
                    style={{
                        flex: 1,
                        justifyContent: actionType === "canceled" ? "center" : undefined,
                    }}
                >
                    {messageComponent}
                    {messageInfo && <MessageInfoDisplay messageInfo={messageInfo} />}

                    {actionType == "start" && (
                        <MarkdownView markdown={i18n.t("screens.contract_signing.description")} />
                    )}

                    {messageInfo?.mode == "error" && (
                        <VStack gap={1}>
                            <Button
                                label={i18n.t("screens.contract_signing.contact_support")}
                                processing={reseting}
                                onPress={() => {
                                    void presentIntercom();
                                }}
                            />
                        </VStack>
                    )}
                </VStack>
            )}
        </ScreenCard>
    );
};

export default ContractSigningScreen;

const useStyles = () => {
    const { fonts, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                loadingText: {
                    ...fonts.regular,
                    textAlign: "center",
                },
                errorText: {
                    marginTop: gridSize * 2,
                    ...fonts.regular,
                    textAlign: "center",
                },
            }),
        [fonts.regular, gridSize],
    );
};
